import { useState } from 'react'
import BookingModal from '../components/BookingModal'
import FontAwesomeIcon from '../components/FontAwesomeIcon'
import { Link } from 'react-router-dom'

export default function KidepoValley() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedExperience, setSelectedExperience] = useState('')

  const handleBooking = (experience) => {
    setSelectedExperience(experience)
    setIsModalOpen(true)
  }

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-80 md:h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(/images/kidepo-hero.jpg)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
            Kidepo Valley National Park
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto">
            Africa's True Wilderness - Uganda's Most Remote Safari
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-safari-green mb-4">Destination Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Kidepo Valley National Park is one of Africa's most spectacular wilderness areas, located in Uganda's
                remote northeastern corner in Karamoja region, approximately 700 kilometers from Kampala. Bordering
                South Sudan to the northwest and Kenya to the east, this 1,442 square kilometer park is Uganda's
                third-largest and arguably its most scenic.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                CNN Travel named Kidepo among Africa's most beautiful parks for good reason. The park's dramatic
                semi-arid savanna landscape features wide-open plains, mountain ranges, and seasonal rivers creating
                exceptional wildlife viewing. With 77 mammal species (including unique wildlife found nowhere else in
                Uganda), 475 bird species, and Karamojong cultural encounters, Kidepo delivers authentic African
                wilderness experiences with minimal tourist crowds.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Size</h3>
                  <p className="text-gray-700">1,442 km²</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Distance from Kampala</h3>
                  <p className="text-gray-700">700 km (10-12 hours)</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Mammal Species</h3>
                  <p className="text-gray-700">77 species</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Bird Species</h3>
                  <p className="text-gray-700">475+</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="/images/kidepo-hero.jpg"
                alt="Kidepo Valley National Park"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How to Get There */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">How to Get There</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-plane" className="w-6 h-6" />
                  By Air (Recommended)
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Kidepo Airstrip:</strong> Daily scheduled flights from Entebbe (2 hours). Most convenient option given distance.</p>
                  <p><strong>Operators:</strong> Aerolink Uganda operates regular flights. Charter flights also available.</p>
                  <p><strong>Cost-Effective:</strong> For groups or limited time, flying is practical alternative to long drive.</p>
                  <p className="text-sm italic">Flight arrives directly at park headquarters. Ground transfers minimal.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-car" className="w-6 h-6" />
                  By Road from Kampala
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Distance:</strong> 700 km via Gulu to Lokumoit Gate</p>
                  <p><strong>Travel Time:</strong> 10-12 hours. 4WD essential for final sections.</p>
                  <p><strong>Route:</strong> Kampala → Luwero → Gulu → Kitgum → Kidepo. Well-maintained tarmac to Kitgum, then murram.</p>
                  <p className="text-sm italic">Long but scenic journey. Most visitors prefer flying given remoteness.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-route" className="w-6 h-6" />
                  Remote Location
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Isolation Advantage:</strong> Remoteness creates authentic wilderness with minimal crowds.</p>
                  <p><strong>From Kenya:</strong> Cross-border access possible via Lokichogio. Requires advance arrangements.</p>
                  <p><strong>Planning Essential:</strong> Book flights, accommodation, and activities well in advance.</p>
                  <p className="text-sm italic">Remoteness is part of the appeal. Proper planning ensures smooth experience.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Key Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-paw" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Unique Wildlife Species</h3>
                <p className="text-gray-700">
                  Only Ugandan park with cheetahs, bat-eared foxes, aardwolves, caracals, and ostriches. These dry-country specialists found nowhere else in Uganda create exceptional wildlife diversity.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-mountain-sun" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Narus Valley</h3>
                <p className="text-gray-700">
                  This seasonal wetland becomes the park's wildlife magnet during dry seasons. Exceptional concentrations of buffalo, elephants, zebras, and predators gather around the only reliable water source.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-mountain" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Dramatic Landscapes</h3>
                <p className="text-gray-700">
                  Golden savanna plains stretch to distant mountain ranges including Mount Morungole and the Napore range. CNN named Kidepo among Africa's most beautiful parks for spectacular scenery.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-fire" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Kanangorok Hot Springs</h3>
                <p className="text-gray-700">
                  Geothermal springs in the Kidepo Valley reach 50°C. The springs create unique ecosystems along seasonal Kidepo River surrounded by distinctive doum palm forests.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-users" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Karamojong Culture</h3>
                <p className="text-gray-700">
                  Semi-nomadic Karamojong people maintain traditional pastoralist lifestyles. Cultural encounters reveal cattle-keeping practices, warrior heritage, traditional dances, and ancient customs.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-dove" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">475 Bird Species</h3>
                <p className="text-gray-700">
                  56 raptor species and dry-country specialists including ostriches (Uganda's only population), secretary birds, kori bustards, Abyssinian ground hornbills, and Karamoja apalis endemic.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities & Experiences */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Activities & Experiences</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-car" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Remote Wilderness Game Drives</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Safari in Uganda's most remote park. Spot ostriches, cheetahs, and large buffalo herds in dramatic arid savanna—species found nowhere else in Uganda.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-5 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $80 per person</p>
                <Link to="/activities/kidepo-game-drives" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-mountain" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Mountain Hiking</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Trek Narus Valley or summit Morungole Mountains with Karamojong warrior guides. Spectacular views and challenging terrain for adventurous hikers.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 4-8 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Strenuous</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $70 per person</p>
                <button onClick={() => handleBooking('Mountain Hiking - Kidepo')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-users" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Cultural Encounters</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Visit Ik or Karamojong villages, traditional cattle camps, and manyattas. Experience authentic pastoralist culture in Uganda's northeastern frontier.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-4 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $40 per person</p>
                <Link to="/activities/karamojong-cultural-encounters" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-binoculars" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Birdwatching Expeditions</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Track 475 bird species across rugged terrain. Spot ostrich, secretary bird, Abyssinian ground hornbill, and dry-country endemics.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-6 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $75 per person</p>
                <button onClick={() => handleBooking('Birdwatching Expeditions - Kidepo')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Travel Tips</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6" />
                  Best Time to Visit
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Dry Seasons (December-March, June-October):</strong> Optimal wildlife viewing as animals concentrate around Narus Valley water sources. Roads easily navigable. Peak season.</p>
                  <p><strong>Wet Seasons (April-May, November):</strong> Lush green landscapes, excellent birding with migrants. Wildlife disperses but scenery spectacular. Fewer visitors.</p>
                  <p className="text-sm italic">Dry season critical for best experience. Wet season roads can be challenging even with 4WD.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                  What to Pack
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Essential:</strong> Sunhat, sunscreen (high SPF), sunglasses, light breathable clothing, warm layers for cool mornings</p>
                  <p><strong>Photography:</strong> Telephoto lens for wildlife, wide-angle for landscapes, extra batteries and memory cards</p>
                  <p><strong>Recommended:</strong> Binoculars, field guides, dust bags for equipment, water bottle, insect repellent</p>
                  <p className="text-sm italic">Semi-arid climate means hot days, cool nights. Dress in layers. Neutral colors for game drives.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Malaria:</strong> Lower risk than tropical areas but prophylaxis still recommended. Use repellent at dawn/dusk.</p>
                  <p><strong>Sun Protection:</strong> Strong equatorial sun. Essential to use high SPF sunscreen, wear hats, stay hydrated.</p>
                  <p><strong>Remoteness:</strong> Limited medical facilities. Ensure comprehensive travel insurance. Bring essential medications.</p>
                  <p><strong>Security:</strong> Park is safe. Karamoja region stable. Follow ranger and lodge guidance.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-coins" className="w-6 h-6" />
                  Budgeting Tips
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Duration:</strong> Minimum 3-4 days recommended given travel distance. Flying essential unless combining with Murchison Falls.</p>
                  <p><strong>Flight Costs:</strong> Budget $250-400 per person for scheduled flights. Worth the investment versus 10-12 hour drive.</p>
                  <p><strong>Accommodation:</strong> Limited options. Book luxury lodges or budget Apoka Bandas well in advance.</p>
                  <p><strong>Best Value:</strong> Visit during dry season for optimal wildlife viewing. Combine cultural experiences with game drives.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Stay */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-6 text-center">Where to Stay</h2>
          <p className="text-center text-gray-700 mb-12 max-w-3xl mx-auto">
            Accommodation options are limited given remoteness. Book well in advance, especially during dry season peak periods.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead className="bg-safari-green text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Category</th>
                  <th className="px-6 py-4 text-left">Accommodation Name</th>
                  <th className="px-6 py-4 text-left">Location</th>
                  <th className="px-6 py-4 text-left">Features</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Apoka Safari Lodge</td>
                  <td className="px-6 py-4">Inside park, Apoka area</td>
                  <td className="px-6 py-4">Luxury tented lodge, pool, gourmet dining, stunning views, exclusive</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Kidepo Savannah Lodge</td>
                  <td className="px-6 py-4">Near park entrance</td>
                  <td className="px-6 py-4">Comfortable cottages, restaurant, bar, valley views, good service</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Nga'Moru Wilderness Camp</td>
                  <td className="px-6 py-4">Inside park</td>
                  <td className="px-6 py-4">Tented camp, en-suite facilities, authentic bush experience</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Adere Safari Lodge</td>
                  <td className="px-6 py-4">Outside park</td>
                  <td className="px-6 py-4">Cottages, restaurant, cultural experiences, affordable</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Apoka Bandas (UWA)</td>
                  <td className="px-6 py-4">Inside park near HQ</td>
                  <td className="px-6 py-4">Basic bandas, communal facilities, budget-friendly, immediate park access</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Camping (UWA)</td>
                  <td className="px-6 py-4">Apoka area</td>
                  <td className="px-6 py-4">Basic camping facilities, bring own equipment, authentic experience</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Is Kidepo worth the journey given its remoteness?</h3>
                <p className="text-gray-700">
                  Absolutely! Kidepo's remoteness is part of its appeal—offering authentic wilderness with minimal crowds. Unique wildlife (cheetahs, ostriches, aardwolves), spectacular scenery, and cultural encounters make it Uganda's most rewarding safari for adventurous travelers.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Should I drive or fly to Kidepo?</h3>
                <p className="text-gray-700">
                  Flying strongly recommended. 10-12 hour drive is exhausting versus 2-hour flight. Aerolink operates daily scheduled flights. For groups of 4+, charter flights become cost-effective. Driving only practical if combining with Murchison Falls.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What makes Kidepo's wildlife unique?</h3>
                <p className="text-gray-700">
                  Kidepo is Uganda's only park with cheetahs, bat-eared foxes, aardwolves, caracals, and ostriches. These dry-country specialists plus 190+ lions, large buffalo herds, and 475 bird species create exceptional diversity unavailable elsewhere in Uganda.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How long should I spend at Kidepo?</h3>
                <p className="text-gray-700">
                  Minimum 3-4 days recommended given travel investment. This allows time for multiple game drives (morning/evening), cultural visits, nature walks, and full appreciation of dramatic landscapes. Serious wildlife enthusiasts benefit from 5-7 days.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">When is the best time for wildlife viewing?</h3>
                <p className="text-gray-700">
                  Dry seasons (December-March, June-October) are optimal. Animals concentrate around Narus Valley water sources creating spectacular viewing. September-November especially good as vegetation thins and wildlife is easiest to spot.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Are there safety concerns in Karamoja region?</h3>
                <p className="text-gray-700">
                  Karamoja region is now stable and safe for tourism. Previous security issues resolved. Park and surrounding areas well-patrolled. Follow lodge and ranger guidance. Millions of travelers visit safely. Focus on enjoying unique cultural and wildlife experiences.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can I combine Kidepo with other parks?</h3>
                <p className="text-gray-700">
                  Best combined with Murchison Falls NP (8-9 hours drive) for northern Uganda circuit. Alternatively, fly to Kidepo then other destinations (Bwindi, Kibale) via Entebbe. Most visitors fly given distances. Multi-park combinations possible with proper planning.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What about the cultural experiences?</h3>
                <p className="text-gray-700">
                  Karamojong cultural encounters are fascinating highlights. Visit traditional manyattas, witness cattle culture central to their lifestyle, see traditional dances, learn warrior heritage. Ik people (mountain dwellers) offer different cultural perspective. Both experiences authentic and memorable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-safari-green text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Discover Africa's Last True Wilderness
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Venture into Uganda's remote northeast and experience the raw beauty of Kidepo Valley. From unique wildlife
            species to ancient cultural traditions and spectacular savanna landscapes, Kidepo offers an authentic African
            adventure far from the beaten path. This is safari at its most pristine.
          </p>
          <Link
            to="/plan-your-trip"
            className="btn-primary text-lg inline-block"
          >
            Plan Your Kidepo Adventure
          </Link>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={selectedExperience}
      />
    </div>
  )
}
